import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Zap,
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  Clock,
  Video,
  Plus,
  Check,
  ExternalLink,
  Filter
} from "lucide-react";

const Events = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [filterIndustry, setFilterIndustry] = useState("");
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    location: "",
    event_type: "networking",
    industries: [],
    max_attendees: "",
    is_virtual: false,
    virtual_link: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [filterIndustry]);

  const fetchEvents = async () => {
    try {
      let url = `${API}/events?upcoming_only=true`;
      if (filterIndustry) {
        url += `&industry=${encodeURIComponent(filterIndustry)}`;
      }
      
      const response = await fetch(url, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error("Failed to fetch events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.date || !newEvent.time) {
      toast.error("Please fill in required fields");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newEvent,
          max_attendees: newEvent.max_attendees ? parseInt(newEvent.max_attendees) : null
        }),
      });

      if (response.ok) {
        toast.success("Event created!");
        setShowCreateEvent(false);
        setNewEvent({
          title: "",
          description: "",
          date: "",
          time: "",
          location: "",
          event_type: "networking",
          industries: [],
          max_attendees: "",
          is_virtual: false,
          virtual_link: ""
        });
        fetchEvents();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to create event");
      }
    } catch (error) {
      toast.error("Failed to create event");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (eventId) => {
    if (!user) {
      toast.error("Please login to register");
      return;
    }

    try {
      const response = await fetch(`${API}/events/${eventId}/register`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setEvents(prev => prev.map(e => 
          e.event_id === eventId 
            ? { 
                ...e, 
                user_registered: data.registered,
                attendees: data.registered 
                  ? [...(e.attendees || []), user.user_id]
                  : (e.attendees || []).filter(id => id !== user.user_id)
              } 
            : e
        ));
        toast.success(data.message);
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to register");
      }
    } catch (error) {
      toast.error("Failed to register");
    }
  };

  const eventTypeColors = {
    networking: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    workshop: "bg-green-500/20 text-green-400 border-green-500/30",
    webinar: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    conference: "bg-crimson/20 text-crimson border-crimson/30"
  };

  const dashboardLink = user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker";

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-6 pt-16 lg:pt-6">
        <div className="max-w-5xl mx-auto">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                INDUSTRY <span className="text-crimson">EVENTS</span>
              </h1>
              <p className="text-gray-400">Connect with professionals and grow your network</p>
            </div>
            {user?.role === "recruiter" && (
              <Button
                onClick={() => setShowCreateEvent(true)}
                className="bg-crimson hover:bg-crimson-hover text-white"
                data-testid="create-event-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Create Event
              </Button>
            )}
          </div>

          {/* Filter */}
          <div className="flex items-center gap-4 mb-8">
            <Filter className="w-5 h-5 text-gray-400" />
            <Input
              value={filterIndustry}
              onChange={(e) => setFilterIndustry(e.target.value)}
              placeholder="Filter by industry..."
              className="bg-obsidian-light border-[#333] text-white placeholder:text-gray-600 max-w-xs"
            />
          </div>

          {/* Create Event Modal */}
          <AnimatePresence>
            {showCreateEvent && (
              <motion.div 
                className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div 
                  className="bg-obsidian-light border border-[#222] p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.9 }}
                >
                  <h3 className="font-heading text-2xl font-bold mb-6">CREATE EVENT</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-400 text-xs uppercase">Title *</Label>
                      <Input
                        value={newEvent.title}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Event title"
                        className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-gray-400 text-xs uppercase">Description</Label>
                      <Textarea
                        value={newEvent.description}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Event description..."
                        className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-400 text-xs uppercase">Date *</Label>
                        <Input
                          type="date"
                          value={newEvent.date}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                          className="bg-obsidian border-[#333] text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-400 text-xs uppercase">Time *</Label>
                        <Input
                          type="time"
                          value={newEvent.time}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                          className="bg-obsidian border-[#333] text-white"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-gray-400 text-xs uppercase">Event Type</Label>
                      <select
                        value={newEvent.event_type}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, event_type: e.target.value }))}
                        className="w-full bg-obsidian border border-[#333] text-white p-2"
                      >
                        <option value="networking">Networking</option>
                        <option value="workshop">Workshop</option>
                        <option value="webinar">Webinar</option>
                        <option value="conference">Conference</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newEvent.is_virtual}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, is_virtual: e.target.checked }))}
                          className="w-4 h-4"
                        />
                        <span className="text-gray-400">Virtual Event</span>
                      </label>
                    </div>

                    {newEvent.is_virtual ? (
                      <div>
                        <Label className="text-gray-400 text-xs uppercase">Virtual Link</Label>
                        <Input
                          value={newEvent.virtual_link}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, virtual_link: e.target.value }))}
                          placeholder="https://zoom.us/..."
                          className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                        />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-gray-400 text-xs uppercase">Location</Label>
                        <Input
                          value={newEvent.location}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="Event location"
                          className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                        />
                      </div>
                    )}

                    <div>
                      <Label className="text-gray-400 text-xs uppercase">Max Attendees (optional)</Label>
                      <Input
                        type="number"
                        value={newEvent.max_attendees}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, max_attendees: e.target.value }))}
                        placeholder="Leave empty for unlimited"
                        className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      onClick={() => setShowCreateEvent(false)}
                      variant="outline"
                      className="flex-1 border-[#333] text-white"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateEvent}
                      disabled={submitting}
                      className="flex-1 bg-crimson hover:bg-crimson-hover text-white"
                    >
                      {submitting ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        "Create Event"
                      )}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Events Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
            </div>
          ) : events.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event, index) => (
                <motion.div
                  key={event.event_id}
                  className="bg-obsidian-light border border-[#222] p-6 hover:border-crimson/50 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`event-${event.event_id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className={`px-2 py-1 text-xs border ${eventTypeColors[event.event_type] || eventTypeColors.networking}`}>
                      {event.event_type?.toUpperCase()}
                    </span>
                    {event.is_virtual && (
                      <Video className="w-4 h-4 text-purple-400" />
                    )}
                  </div>

                  <h3 className="font-heading text-xl font-bold mb-2">{event.title}</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{event.description}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(event.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Clock className="w-4 h-4" />
                      <span>{event.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      {event.is_virtual ? (
                        <>
                          <Video className="w-4 h-4" />
                          <span>Virtual Event</span>
                        </>
                      ) : (
                        <>
                          <MapPin className="w-4 h-4" />
                          <span>{event.location || "TBA"}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Users className="w-4 h-4" />
                      <span>
                        {event.attendees?.length || 0}
                        {event.max_attendees && ` / ${event.max_attendees}`} attending
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleRegister(event.event_id)}
                      variant={event.user_registered ? "outline" : "default"}
                      className={event.user_registered 
                        ? "flex-1 border-crimson text-crimson hover:bg-crimson/10" 
                        : "flex-1 bg-crimson hover:bg-crimson-hover text-white"
                      }
                      data-testid={`register-${event.event_id}`}
                    >
                      {event.user_registered ? (
                        <>
                          <Check className="w-4 h-4 mr-2" /> Registered
                        </>
                      ) : (
                        "Register"
                      )}
                    </Button>
                    {event.is_virtual && event.virtual_link && event.user_registered && (
                      <Button
                        onClick={() => window.open(event.virtual_link, "_blank")}
                        variant="outline"
                        className="border-[#333]"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="font-heading text-2xl font-bold mb-2">No Upcoming Events</h3>
              <p className="text-gray-400 mb-6">Check back later for industry events</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Events;
